import React from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import logoUrl from 'assets/logo_manty_new.png';
import PrintableFilterBar from 'routes/Dashboard/components/WidgetsLayout/PrintableGrid/PrintableFilterBar/PrintableFilterBar';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { GRID_NB_COLS, ROW_HEIGHT, VERTICAL_MARGIN } from './constants';
import { getYOffset, PrintableWidget } from './helpers/paginationHelpers';

type Props = {
    title: string;
    pageIndex: number;
    pageWidgets: PrintableWidget[];
    WidgetRender: (arg1: { widget: PrintableWidget }) => React.ReactElement;
    clientLogoUrl: string | null | undefined;
    filters: DashboardFilterT[];
};

const StyWidgetContainer = styled(Box, { name: 'StyWidgetContainer' })({
    position: 'relative',
    maxWidth: 'none',
});

/*
 * Render one page of the printed PDF.
 * It uses the y RGL coordinates of the children (widgets) and absolute positioning.
 */
const PrintablePage = ({
    title,
    pageIndex,
    pageWidgets,
    WidgetRender,
    clientLogoUrl,
    filters,
}: Props): React.ReactElement => {
    /*
     * This is the function which determines how the widgets will be positioned, using
     * absolute positioning.
     *
     * TODO: separate computation from style.
     */

    const { t } = useTranslation();
    const childrenStyle = (widget: PrintableWidget): React.CSSProperties => {
        const widgetLayout = widget.position.md;
        const height =
            (ROW_HEIGHT + VERTICAL_MARGIN) * widgetLayout.h - VERTICAL_MARGIN;
        const originalHeight =
            (ROW_HEIGHT + VERTICAL_MARGIN) *
                (widget.originalHeight ?? widgetLayout.h) -
            VERTICAL_MARGIN;
        // Each page starts at 0, so we need to offset y with the size of the previous pages.
        const yOffset = getYOffset(pageIndex);
        if (widgetLayout.x === undefined || widgetLayout.y === undefined) {
            throw new Error(
                `PrintablePage error - undefined position for widget {key}`,
            );
        }
        const baseStyle: React.CSSProperties = {
            position: 'absolute',
            marginTop: `${VERTICAL_MARGIN}px`,
            height,
            left: `calc(100% * ${widgetLayout.x / GRID_NB_COLS})`,
            top: (widgetLayout.y - yOffset) * (ROW_HEIGHT + VERTICAL_MARGIN),
            width: `calc(100% * ${widgetLayout.w / GRID_NB_COLS})`,
        };
        const duplicatedStyle = {
            ...baseStyle,
            // for duplicated widget we adapt the top position with a overflow hidden
            // so that only the part that is truncated from the original widget is showed
            // for the top position, we set it at the widget height - original widget height
            // to let only the original widget truncated part visible
            zIndex: -1,
            top: height - originalHeight,
            height: originalHeight,
        };
        return widget.isTruncatedDuplicate ? duplicatedStyle : baseStyle;
    };

    return (
        <Box className="printable-page">
            <Box
                className="logo-print"
                sx={{
                    background: 'white',
                    pt: '0.5cm',
                    position: 'relative',
                    zIndex: 999999999, // dirty hack to make sure the logo is on top of everything else
                }}
            >
                <img
                    alt=""
                    src={clientLogoUrl || logoUrl}
                    className="client-logo-print"
                />
                <img alt="" src={logoUrl} className="manty-logo-print" />
            </Box>
            {pageIndex === 0 && (
                <>
                    <Typography
                        color="textPrimary"
                        variant="body1Bold"
                        align="center"
                        paragraph
                    >
                        {title}
                    </Typography>
                    <Typography color="textPrimary" variant="body1" paragraph>
                        {`${t(
                            'printPage.extractionDate',
                        )}: ${new Date().toLocaleDateString()}`}
                    </Typography>
                </>
            )}
            <PrintableFilterBar filters={filters} />
            <StyWidgetContainer>
                {pageWidgets.map((widget) => (
                    <Box
                        className="printable-grid-element"
                        sx={childrenStyle(widget)}
                        key={widget.key}
                    >
                        {WidgetRender({ widget })}
                    </Box>
                ))}
            </StyWidgetContainer>
        </Box>
    );
};

export default PrintablePage;
